import { useEffect, useMemo, useState } from 'react'

import { TableContext } from './TableContext'
import TableCore from './TableCore'
import { usePageSize, useSort, useTableColumnsStorage } from './table.hooks'
import type { ITablesExtendsData, ResizeData, TableProps } from './types'
import { extractUserSettingsFromColumns, mergeColumnSettings } from './utils'

const Table = <T extends ITablesExtendsData>(props: TableProps<T>) => {
  const {
    name,
    dataSource,
    total,
    defaultSort = 'updatedAt',
    onLoadMore,
    hasNextPage,
    columns: baseColumns,
    resizeableColumns = true,
  } = props
  const { page, pageSize, setPage } = usePageSize(name)
  const { sort, sortOrder, setSort } = useSort(name, defaultSort as string)
  const { tableColumnsState, setTableColumnsState } = useTableColumnsStorage(name)
  const [columns, setColumns] = useState(mergeColumnSettings(baseColumns, tableColumnsState))

  const handleResize = (index: number, data: ResizeData) => {
    if (!resizeableColumns) {
      return
    }
    const next = [...columns]
    const col = { ...next[index] }
    col.width = data.width
    next[index] = col
    setColumns(next)

    if (data.status === 'RELEASE') {
      const newTableSettings = extractUserSettingsFromColumns(next)
      setTableColumnsState(newTableSettings)
    }
  }

  const value = useMemo(
    () => ({
      pagination: {
        page,
        pageSize,
        setPage,
        total: total || dataSource.length,
      },
      sort: {
        sortKey: sort,
        sortOrder,
        setSort,
      },
      onLoadMore,
      hasNextPage,
    }),
    [
      page,
      pageSize,
      setPage,
      total,
      dataSource.length,
      sort,
      sortOrder,
      setSort,
      onLoadMore,
      hasNextPage,
    ],
  )

  useEffect(() => {
    setColumns((cols) => mergeColumnSettings(cols, tableColumnsState))
  }, [tableColumnsState, setColumns])

  return (
    <TableContext.Provider value={value}>
      <TableCore {...props} columns={columns} onColumnResize={handleResize} />
    </TableContext.Provider>
  )
}

export default Table
